import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
// utils
import { fToNow } from 'src/utils/format-time';
// components
import Iconify from 'src/components/iconify';
import { Button, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

export default function NotificationItem({ notification, markNotificationAsRead, deleteNotification }) {
  const [isRead, setIsRead] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setAnchorEl(null);
  };
  
  const renderTitleAndCreatedDate = (
    <ListItemText
      disableTypography
      primary={
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            pb: 0.5,
            color: 'black',
            pr: 4
          }}
        >
          {notification.title}
          {!notification.readAt && !isRead && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'info.main',
                ml: 1,
              }}
            />
          )}
        </Typography>
      }
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%'
              }}
            />
          }
        >
          {fToNow(notification.createdAt)}
        </Stack>
      }
    />
  );

  const renderActions = (
    <IconButton 
      onClick={handleOpenMenu}
      sx={{
        position: 'absolute',
        right: 10,
        top: 13
      }}
    >
      <Iconify icon="eva:more-horizontal-fill" />
    </IconButton>
  );

  const renderMessage = (
    <Stack alignItems="flex-start">
      <Box
        sx={{
          p: 1.5,
          mt: 1,
          width: '100%',
          borderRadius: 1.5,
          color: 'text.secondary',
          bgcolor: 'background.neutral',
        }}
      >
        <Typography
          variant='caption'
        >
          {notification.message}
        </Typography>
      </Box>
    </Stack>
  );

  const getRedirection = useCallback(() => {
    switch (notification.type) { 
      // 1. Profile Docs
      case "PROFILE_DOCS_SUBMITTED":
      case "PROFILE_DOCS_REJECTED": 
        return paths.dashboard.user.endUserDocumentSubmission;

      // 2. Vehicle Submissions
      case "VEHICLE_SUBMITTED":
      case "VEHICLE_UPDATE_SUBMITTED":
        return paths.dashboard.vehicle.listing;

      // 3. Disputes & Claims
      case "DISPUTE_OPENED":
      case "CLAIM_SUBMITTED":
        return paths.dashboard.bookings.bookings; 

      // 5, 6, 7. Reportings
      case "REPORT_CUSTOMER_SUBMITTED":
        return paths.dashboard.support.customerReporting;
      case "REPORT_VENDOR_SUBMITTED":
        return paths.dashboard.support.vendorReporting;
      case "REPORT_VEHICLE_SUBMITTED":
        return paths.dashboard.vehicle.all; 

      // 8. Chat
      case "CHAT_MESSAGE":
        return paths.dashboard.support.chat; 

      default:
        return null;
    }
  }, [notification]);

  return (
    <Link 
      to={getRedirection()}
      style={{
        textDecoration: 'none'
      }}
    >
      <ListItemButton
        disableRipple
        sx={{
          p: 2.5,
          alignItems: 'flex-start',
          borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
          transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
          cursor: getRedirection() ? 'pointer' : 'default',
          "&:hover": {
            transform: getRedirection() ? 'scale(0.95)' : 'none', 
            backgroundColor: notification.category ? "#eeeeee86" : "transparent",
          }
        }}
      >
        {renderActions}

        <Stack sx={{ flexGrow: 1 }}>
          {renderTitleAndCreatedDate}
          {renderMessage}
        </Stack>
      </ListItemButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(!notification.readAt && !isRead) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markNotificationAsRead(notification.id);
              setIsRead(true);
              handleCloseMenu(e);
            }}
            sx={{ typography: 'body2' }}
          >
            <Iconify icon="fluent:mail-read-32-regular" width={20} sx={{ mr: 2 }} />
            Mark as read
          </MenuItem>
        )}
        
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteNotification(notification.id);
            handleCloseMenu(e);
          }}
          sx={{ typography: 'body2', color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" width={20} sx={{ mr: 2, color: 'error.main' }} />
          Delete
        </MenuItem>
      </Menu>
    </Link>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object,
  markNotificationAsRead: PropTypes.func
};